import React, {useCallback} from 'react';

import {BulkActionDownload, BulkActionResultModal} from 'src/components/bulk-tool';
import {CompleteMark} from 'src/components/complete-mark';
import {UserLink} from 'src/components/links';
import {RowNumbered} from 'src/components/row-numbered';
import {getCrmUpdateBulkResult, getUserCrmUpdateProgress} from 'src/openapi/admin/apis/BulkCrmUpdateApi';
import {useAppDispatch} from 'src/store/hooks';

import {Group} from '@pb/kit-component-group';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';


import {FILE_NAME, PB_HEADERS, TOOL_TITLE} from './constants';
import {getFileHeaders} from './utils/getFileHeaders';

import type {CrmIdProcessedItem} from 'src/openapi/admin/models/CrmIdProcessedItem';
import type {Pagination} from 'src/openapi/admin/models/Pagination';

type Props = {
    id: string,
    onClose: () => void,
};

export const UpdatCrmIdsResultModal = ({id, onClose}: Props) => {
    const apiDispatch = useAppDispatch();

    const getPaginatedResults = useCallback(
        (bulkResult: CrmIdProcessedItem[], {currentPage, pageSize}: Pagination) => bulkResult
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map((res: CrmIdProcessedItem, index) => (
                <Row key={index}>
                    <Cell>
                        <RowNumbered
                            maxRowNumber={bulkResult?.length || 0}
                            placeholder={(currentPage - 1) * pageSize + index + 1}
                        />
                    </Cell>
                    <Cell>
                        <UserLink
                            userId={res.userId || 0}
                            caption={res.userEmail}
                            blank={true}
                            place={`Bulk ${TOOL_TITLE} Result`}
                        />
                    </Cell>
                    <Cell>{res.oldValue || '--'}</Cell>
                    <Cell>{res.newValue || '--'}</Cell>
                    <Cell>
                        <CompleteMark errors={res.errors}/>
                    </Cell>
                </Row>
            )),
        [],
    );

    return (
        <BulkActionResultModal<CrmIdProcessedItem>
            id={id}
            onClose={onClose}

            toolTitle={TOOL_TITLE}
            description={bulkResult => (
                <Group justify="space-between" alignItems="center">
                    <b>Update results</b>
                    {!!bulkResult.itemList && (
                        <BulkActionDownload
                            toolName={TOOL_TITLE}
                            fileName={FILE_NAME}
                            startTime={bulkResult.startTime}
                            headers={getFileHeaders(bulkResult.itemList[0].crmType)}
                            body={bulkResult.itemList?.map(item => ({
                                userId: item.userId.toString(),
                                userEmail: item.userEmail,
                                oldValue: item.oldValue,
                                newValue: item.newValue,
                                actionStatus: item.actionStatus,
                                errors: item.errors?.join('. '),
                            })) as Record<string, string>[]}
                        />
                    )}
                </Group>
            )}
            table={(bulkResult, pagination) => (
                <Table borderedGrid={true}>
                    <Head>
                        <Cell width={50}><strong>#</strong></Cell>
                        <Cell width={246}>User Email</Cell>
                        <Cell width={200}>Old {PB_HEADERS[bulkResult[0].crmType]}</Cell>
                        <Cell width={200}>New {PB_HEADERS[bulkResult[0].crmType]}</Cell>
                        <Cell width={150}>Action Status</Cell>
                    </Head>
                    <Body>
                        {getPaginatedResults(bulkResult, pagination)}
                    </Body>
                </Table>
            )}

            getProgress={() => apiDispatch(getUserCrmUpdateProgress({id}))}
            getResult={() => apiDispatch(getCrmUpdateBulkResult({id}))}

            sortComparator={(a, b) => a.userEmail.localeCompare(b.userEmail)}
        />
    );
};
